import {
  getAppointmentLocalDateWeekday,
  isAppointmentLocalDate,
  isAppointmentLocalTime,
} from "../scheduling/appointmentAvailabilityProvider";
import type { AppointmentSchedulePlan } from "../scheduling/appointmentSchedulePlan";

export const APPOINTMENT_WAITLIST_CONFIGURATION_SCHEMA_VERSION = 1 as const;
export const APPOINTMENT_WAITLIST_CONFLICT_SCHEMA_VERSION = 1 as const;
export const APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES = 15 as const;
export const APPOINTMENT_WAITLIST_MAX_CONFIGURATION_TTL_MS = 60 * 60_000;
export const APPOINTMENT_WAITLIST_MAX_BOOKING_WINDOW_DAYS = 366;
export type AppointmentWaitlistLocale = "en" | "ro";

export type AppointmentWaitlistWeekday =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type AppointmentWaitlistDaypart = "morning" | "afternoon" | "evening";

export type AppointmentWaitlistPreferredTimeScope =
  "any-time" | "business-hours-only";

export type AppointmentWaitlistTimePreference =
  | { kind: "any" }
  | { kind: "daypart"; daypart: AppointmentWaitlistDaypart }
  | { kind: "window"; startTime: string; endTime: string };

export interface AppointmentWaitlistDateTimePreference {
  date: string;
  time: AppointmentWaitlistTimePreference;
}

export interface AppointmentWaitlistPreferences {
  dateTimePreferences: readonly AppointmentWaitlistDateTimePreference[];
}

export interface AppointmentWaitlistProviderIdentity {
  providerId: string;
  providerRevision: string;
  source: "live" | "frontend-fixture";
}

export interface AppointmentWaitlistConfigurationPublicationV1 {
  schemaVersion: typeof APPOINTMENT_WAITLIST_CONFIGURATION_SCHEMA_VERSION;
  kind: "appointment-waitlist-configuration";
  publicationId: string;
  waitlistDraftId: string;
  sourceAppointmentDraftId: string;
  locationId: string;
  planVersion: 1;
  plan: AppointmentSchedulePlan;
  planFingerprint: string;
  timeZone: string;
  locale: AppointmentWaitlistLocale;
  providerIdentity: AppointmentWaitlistProviderIdentity;
  generatedAtEpochMs: number;
  expiresAtEpochMs: number;
  bookingWindow: { earliestDate: string; latestDate: string };
  businessHours: readonly {
    weekday: AppointmentWaitlistWeekday;
    intervals: readonly { startTime: string; endTime: string }[];
  }[];
  timeRules: {
    preferredTimeScope: AppointmentWaitlistPreferredTimeScope;
    exposesAnyTime: boolean;
    incrementMinutes: typeof APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES;
    dayparts: readonly {
      daypart: AppointmentWaitlistDaypart;
      startTime: string;
      endTime: string;
    }[];
  };
  firstVisit:
    | {
        kind: "required";
        questionId: string;
        businessNameSnapshot: string;
      }
    | { kind: "not-required" };
  paymentPresentation: {
    kind: "pay-at-venue";
    chargeNowAmountMinor: 0;
    copy: {
      en: { description: string; methodLabel: string };
      ro: { description: string; methodLabel: string };
    };
  };
}

export interface AppointmentWaitlistConflictPublicationV1 {
  schemaVersion: typeof APPOINTMENT_WAITLIST_CONFLICT_SCHEMA_VERSION;
  kind: "appointment-waitlist-conflict";
  conflictPublicationId: string;
  publicationId: string;
  waitlistDraftId: string;
  providerIdentity: AppointmentWaitlistProviderIdentity;
  generatedAtEpochMs: number;
  expiresAtEpochMs: number;
  preferencesFingerprint: string;
  result:
    | { kind: "none" }
    | {
        kind: "bookable";
        matches: readonly {
          preferenceIndex: number;
          date: string;
        }[];
      };
}

export interface AppointmentWaitlistTimeOptionsForDate {
  date: string;
  preferredTimeScope: AppointmentWaitlistPreferredTimeScope;
  allowsAny: boolean;
  intervals: readonly { startTime: string; endTime: string }[];
  dayparts: readonly {
    daypart: AppointmentWaitlistDaypart;
    startTime: string;
    endTime: string;
  }[];
  startTimes: readonly string[];
  endTimes: readonly string[];
}

const WEEKDAYS: readonly AppointmentWaitlistWeekday[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const APPOINTMENT_WAITLIST_DAYPARTS = [
  { daypart: "morning", startTime: "06:00", endTime: "12:00" },
  { daypart: "afternoon", startTime: "12:00", endTime: "17:00" },
  { daypart: "evening", startTime: "17:00", endTime: "24:00" },
] as const satisfies AppointmentWaitlistConfigurationPublicationV1["timeRules"]["dayparts"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

function isBoundedText(value: unknown, maximum = 500): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= maximum
  );
}

function isEpoch(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isIanaTimeZone(value: unknown): value is string {
  if (!isIdentifier(value)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}

function isProviderIdentity(
  value: unknown,
): value is AppointmentWaitlistProviderIdentity {
  return (
    isRecord(value) &&
    isIdentifier(value.providerId) &&
    isIdentifier(value.providerRevision) &&
    (value.source === "live" || value.source === "frontend-fixture")
  );
}

function providerIdentitiesMatch(
  left: AppointmentWaitlistProviderIdentity,
  right: AppointmentWaitlistProviderIdentity,
) {
  return (
    left.providerId === right.providerId &&
    left.providerRevision === right.providerRevision &&
    left.source === right.source
  );
}

function haveExactSerializableStructure(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function hashFingerprint(value: string, seed: number) {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16_777_619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export function createAppointmentWaitlistSchedulePlanFingerprint(
  plan: AppointmentSchedulePlan,
) {
  const serialized = JSON.stringify(plan);
  return `appointment-waitlist-plan-v1-${[
    0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35,
  ]
    .map((seed) => hashFingerprint(serialized, seed))
    .join("")}`;
}

function timeToMinutes(value: string, terminalEnd = false) {
  if (terminalEnd && value === "24:00") return 24 * 60;
  if (!isAppointmentLocalTime(value)) return null;
  const [hour, minute] = value.split(":").map(Number);
  return hour! * 60 + minute!;
}

function isIncrementTime(value: string, terminalEnd = false) {
  const minutes = timeToMinutes(value, terminalEnd);
  return (
    minutes !== null &&
    minutes % APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES === 0
  );
}

function cloneInterval(value: unknown) {
  if (
    !isRecord(value) ||
    typeof value.startTime !== "string" ||
    typeof value.endTime !== "string"
  ) {
    return null;
  }
  const start = timeToMinutes(value.startTime);
  const end = timeToMinutes(value.endTime, true);
  return start !== null &&
    end !== null &&
    start < end &&
    isIncrementTime(value.startTime) &&
    isIncrementTime(value.endTime, true)
    ? { startTime: value.startTime, endTime: value.endTime }
    : null;
}

function validateAppointmentWaitlistConfigurationPublicationForMode(
  value: unknown,
  expectedMode: AppointmentSchedulePlan["mode"],
): AppointmentWaitlistConfigurationPublicationV1 | null {
  if (
    !isRecord(value) ||
    value.schemaVersion !== APPOINTMENT_WAITLIST_CONFIGURATION_SCHEMA_VERSION ||
    value.kind !== "appointment-waitlist-configuration" ||
    !isIdentifier(value.publicationId) ||
    !isIdentifier(value.waitlistDraftId) ||
    !isIdentifier(value.sourceAppointmentDraftId) ||
    !isIdentifier(value.locationId) ||
    value.planVersion !== 1 ||
    !isRecord(value.plan) ||
    value.plan.planVersion !== 1 ||
    value.plan.mode !== expectedMode ||
    value.plan.draftId !== value.sourceAppointmentDraftId ||
    value.plan.locationId !== value.locationId ||
    value.plan.timeZone !== value.timeZone ||
    !isIdentifier(value.planFingerprint) ||
    !isIanaTimeZone(value.timeZone) ||
    (value.locale !== "en" && value.locale !== "ro") ||
    !isProviderIdentity(value.providerIdentity) ||
    !isEpoch(value.generatedAtEpochMs) ||
    !isEpoch(value.expiresAtEpochMs) ||
    value.expiresAtEpochMs <= value.generatedAtEpochMs ||
    value.expiresAtEpochMs - value.generatedAtEpochMs >
      APPOINTMENT_WAITLIST_MAX_CONFIGURATION_TTL_MS ||
    !isRecord(value.bookingWindow) ||
    !isAppointmentLocalDate(value.bookingWindow.earliestDate) ||
    !isAppointmentLocalDate(value.bookingWindow.latestDate) ||
    value.bookingWindow.earliestDate > value.bookingWindow.latestDate ||
    (() => {
      const earliest = new Date(`${value.bookingWindow.earliestDate}T00:00:00Z`);
      const latest = new Date(`${value.bookingWindow.latestDate}T00:00:00Z`);
      return (
        (latest.getTime() - earliest.getTime()) / 86_400_000 + 1 >
        APPOINTMENT_WAITLIST_MAX_BOOKING_WINDOW_DAYS
      );
    })() ||
    !Array.isArray(value.businessHours) ||
    value.businessHours.length !== WEEKDAYS.length ||
    !isRecord(value.timeRules) ||
    (value.timeRules.preferredTimeScope !== "any-time" &&
      value.timeRules.preferredTimeScope !== "business-hours-only") ||
    typeof value.timeRules.exposesAnyTime !== "boolean" ||
    value.timeRules.incrementMinutes !==
      APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES ||
    !Array.isArray(value.timeRules.dayparts) ||
    JSON.stringify(value.timeRules.dayparts) !==
      JSON.stringify(APPOINTMENT_WAITLIST_DAYPARTS) ||
    !isRecord(value.firstVisit) ||
    !isRecord(value.paymentPresentation) ||
    value.paymentPresentation.kind !== "pay-at-venue" ||
    value.paymentPresentation.chargeNowAmountMinor !== 0 ||
    !isRecord(value.paymentPresentation.copy)
  ) {
    return null;
  }

  const plan = value.plan as unknown as AppointmentSchedulePlan;
  if (
    value.planFingerprint !==
    createAppointmentWaitlistSchedulePlanFingerprint(plan)
  ) {
    return null;
  }

  const paymentCopy =
    {} as AppointmentWaitlistConfigurationPublicationV1["paymentPresentation"]["copy"];
  for (const locale of ["en", "ro"] as const) {
    const copy = value.paymentPresentation.copy[locale];
    if (
      !isRecord(copy) ||
      !isBoundedText(copy.description, 1_000) ||
      !isBoundedText(copy.methodLabel, 120)
    ) {
      return null;
    }
    paymentCopy[locale] = {
      description: copy.description,
      methodLabel: copy.methodLabel,
    };
  }

  const businessHours: AppointmentWaitlistConfigurationPublicationV1["businessHours"][number][] =
    [];
  for (const [index, row] of value.businessHours.entries()) {
    if (
      !isRecord(row) ||
      row.weekday !== WEEKDAYS[index] ||
      !Array.isArray(row.intervals)
    )
      return null;
    const intervals = row.intervals.map(cloneInterval);
    if (intervals.some((interval) => interval === null)) return null;
    const accepted = intervals as { startTime: string; endTime: string }[];
    if (
      accepted.some(
        (interval, intervalIndex) =>
          intervalIndex > 0 &&
          accepted[intervalIndex - 1]!.endTime > interval.startTime,
      )
    )
      return null;
    businessHours.push({
      weekday: row.weekday as AppointmentWaitlistWeekday,
      intervals: accepted,
    });
  }

  const firstVisit =
    value.firstVisit.kind === "not-required"
      ? { kind: "not-required" as const }
      : value.firstVisit.kind === "required" &&
          isIdentifier(value.firstVisit.questionId) &&
          isBoundedText(value.firstVisit.businessNameSnapshot)
        ? {
            kind: "required" as const,
            questionId: value.firstVisit.questionId,
            businessNameSnapshot: value.firstVisit.businessNameSnapshot,
          }
        : null;
  if (!firstVisit) return null;

  return {
    schemaVersion: APPOINTMENT_WAITLIST_CONFIGURATION_SCHEMA_VERSION,
    kind: "appointment-waitlist-configuration",
    publicationId: value.publicationId,
    waitlistDraftId: value.waitlistDraftId,
    sourceAppointmentDraftId: value.sourceAppointmentDraftId,
    locationId: value.locationId,
    planVersion: 1,
    plan: JSON.parse(JSON.stringify(plan)) as AppointmentSchedulePlan,
    planFingerprint: value.planFingerprint,
    timeZone: value.timeZone,
    locale: value.locale,
    providerIdentity: { ...value.providerIdentity },
    generatedAtEpochMs: value.generatedAtEpochMs,
    expiresAtEpochMs: value.expiresAtEpochMs,
    bookingWindow: {
      earliestDate: value.bookingWindow.earliestDate,
      latestDate: value.bookingWindow.latestDate,
    },
    businessHours,
    timeRules: {
      preferredTimeScope: value.timeRules.preferredTimeScope,
      exposesAnyTime: value.timeRules.exposesAnyTime,
      incrementMinutes: APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES,
      dayparts: APPOINTMENT_WAITLIST_DAYPARTS.map((row) => ({ ...row })),
    },
    firstVisit,
    paymentPresentation: {
      kind: "pay-at-venue",
      chargeNowAmountMinor: 0,
      copy: paymentCopy,
    },
  };
}

export function validateAppointmentWaitlistConfigurationPublication(
  value: unknown,
): AppointmentWaitlistConfigurationPublicationV1 | null {
  return validateAppointmentWaitlistConfigurationPublicationForMode(
    value,
    "individual",
  );
}

/**
 * Storage-retirement compatibility only. Group Waitlist is not a current
 * product capability; this validator exists solely so the one-way migration
 * can distinguish a complete historical Group publication from hostile bytes.
 */
export function validateRetiredGroupAppointmentWaitlistConfigurationPublicationForStorageMigration(
  value: unknown,
): AppointmentWaitlistConfigurationPublicationV1 | null {
  return validateAppointmentWaitlistConfigurationPublicationForMode(
    value,
    "group",
  );
}

function isAppointmentWaitlistConfigurationPublicationCurrentForMode(input: {
  publication: AppointmentWaitlistConfigurationPublicationV1;
  expectedProviderIdentity: AppointmentWaitlistProviderIdentity;
  evaluatedAtEpochMs?: number;
  expectedMode: AppointmentSchedulePlan["mode"];
}) {
  const publication = validateAppointmentWaitlistConfigurationPublicationForMode(
    input.publication,
    input.expectedMode,
  );
  const now = input.evaluatedAtEpochMs ?? Date.now();
  return (
    Boolean(publication) &&
    isEpoch(now) &&
    providerIdentitiesMatch(
      publication!.providerIdentity,
      input.expectedProviderIdentity,
    ) &&
    publication!.generatedAtEpochMs <= now + 5 * 60_000 &&
    publication!.expiresAtEpochMs > now
  );
}

export function isAppointmentWaitlistConfigurationPublicationCurrent(input: {
  publication: AppointmentWaitlistConfigurationPublicationV1;
  expectedProviderIdentity: AppointmentWaitlistProviderIdentity;
  evaluatedAtEpochMs?: number;
}) {
  return isAppointmentWaitlistConfigurationPublicationCurrentForMode({
    ...input,
    expectedMode: "individual",
  });
}

export function isRetiredGroupAppointmentWaitlistConfigurationPublicationCurrentForStorageMigration(
  input: {
    publication: AppointmentWaitlistConfigurationPublicationV1;
    expectedProviderIdentity: AppointmentWaitlistProviderIdentity;
    evaluatedAtEpochMs?: number;
  },
) {
  return isAppointmentWaitlistConfigurationPublicationCurrentForMode({
    ...input,
    expectedMode: "group",
  });
}

/** Stable presentation identity for an already validated configuration. Fresh
 * publication/TTL instances do not discard an open editor; every policy,
 * provider, source, locale and scheduling field remains part of the identity.
 * This key never substitutes for the current publication checks at mutation. */
export function getAppointmentWaitlistConfigurationAuthorityKey(value: unknown): string | null {
  const publication = validateAppointmentWaitlistConfigurationPublication(value);
  if (!publication) return null;
  const {
    publicationId: omittedPublicationId,
    generatedAtEpochMs: omittedGeneratedAt,
    expiresAtEpochMs: omittedExpiresAt,
    ...authority
  } = publication;
  void omittedPublicationId;
  void omittedGeneratedAt;
  void omittedExpiresAt;
  return JSON.stringify(authority);
}

export function haveExactAppointmentWaitlistConfigurationAuthority(
  leftValue: unknown,
  rightValue: unknown,
) {
  const left = getAppointmentWaitlistConfigurationAuthorityKey(leftValue);
  const right = getAppointmentWaitlistConfigurationAuthorityKey(rightValue);
  return left !== null && right !== null && left === right;
}

function enumerateTimes(
  start: number,
  end: number,
  includeStart: boolean,
  includeEnd: boolean,
) {
  const values: string[] = [];
  const first = includeStart
    ? start
    : start + APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES;
  const last = includeEnd
    ? end
    : end - APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES;
  for (
    let minutes = first;
    minutes <= last;
    minutes += APPOINTMENT_WAITLIST_TIME_INCREMENT_MINUTES
  ) {
    values.push(
      minutes === 24 * 60
        ? "24:00"
        : `${Math.floor(minutes / 60)
            .toString()
            .padStart(2, "0")}:${(minutes % 60).toString().padStart(2, "0")}`,
    );
  }
  return values;
}

function resolveAppointmentWaitlistTimeOptionsForDateForMode(
  publicationValue: AppointmentWaitlistConfigurationPublicationV1,
  date: string,
  expectedMode: AppointmentSchedulePlan["mode"],
): AppointmentWaitlistTimeOptionsForDate | null {
  const publication = validateAppointmentWaitlistConfigurationPublicationForMode(
    publicationValue,
    expectedMode,
  );
  if (
    !publication ||
    !isAppointmentLocalDate(date) ||
    date < publication.bookingWindow.earliestDate ||
    date > publication.bookingWindow.latestDate
  )
    return null;
  const weekday = getAppointmentLocalDateWeekday(
    date,
  ) as AppointmentWaitlistWeekday | null;
  const intervals =
    publication.timeRules.preferredTimeScope === "any-time"
      ? [{ startTime: "00:00", endTime: "24:00" }]
      : (publication.businessHours.find((row) => row.weekday === weekday)
          ?.intervals ?? []);
  const startTimes = [
    ...new Set(
      intervals.flatMap((interval) =>
        enumerateTimes(
          timeToMinutes(interval.startTime)!,
          timeToMinutes(interval.endTime, true)!,
          true,
          false,
        ),
      ),
    ),
  ];
  const endTimes = [
    ...new Set(
      intervals.flatMap((interval) =>
        enumerateTimes(
          timeToMinutes(interval.startTime)!,
          timeToMinutes(interval.endTime, true)!,
          false,
          true,
        ),
      ),
    ),
  ];
  const dayparts = publication.timeRules.dayparts.filter((daypart) => {
    const start = timeToMinutes(daypart.startTime)!;
    const end = timeToMinutes(daypart.endTime, true)!;
    return intervals.some(
      (interval) =>
        timeToMinutes(interval.startTime)! <= start &&
        timeToMinutes(interval.endTime, true)! >= end,
    );
  });
  return {
    date,
    preferredTimeScope: publication.timeRules.preferredTimeScope,
    allowsAny: publication.timeRules.exposesAnyTime && intervals.length > 0,
    intervals: intervals.map((interval) => ({ ...interval })),
    dayparts,
    startTimes,
    endTimes,
  };
}

export function resolveAppointmentWaitlistTimeOptionsForDate(
  publicationValue: AppointmentWaitlistConfigurationPublicationV1,
  date: string,
) {
  return resolveAppointmentWaitlistTimeOptionsForDateForMode(
    publicationValue,
    date,
    "individual",
  );
}

function preferenceIsAllowed(
  preference: AppointmentWaitlistDateTimePreference,
  options: AppointmentWaitlistTimeOptionsForDate,
) {
  const { time } = preference;

  if (time.kind === "any") return options.allowsAny;
  if (time.kind === "daypart") {
    return options.dayparts.some(({ daypart }) => daypart === time.daypart);
  }
  const start = timeToMinutes(time.startTime);
  const end = timeToMinutes(time.endTime, true);
  return (
    start !== null &&
    end !== null &&
    start < end &&
    isIncrementTime(time.startTime) &&
    isIncrementTime(time.endTime, true) &&
    options.startTimes.includes(time.startTime) &&
    options.endTimes.includes(time.endTime) &&
    options.intervals.some(
      (interval) =>
        timeToMinutes(interval.startTime)! <= start &&
        timeToMinutes(interval.endTime, true)! >= end,
    )
  );
}

function validateAppointmentWaitlistPreferencesForConfigurationForMode(input: {
  publication: AppointmentWaitlistConfigurationPublicationV1;
  preferences: AppointmentWaitlistPreferences;
  maximumPreferences?: number;
  expectedMode: AppointmentSchedulePlan["mode"];
}) {
  const maximum = input.maximumPreferences ?? 6;
  if (
    !Array.isArray(input.preferences.dateTimePreferences) ||
    input.preferences.dateTimePreferences.length < 1 ||
    input.preferences.dateTimePreferences.length > maximum
  )
    return null;
  const accepted: AppointmentWaitlistDateTimePreference[] = [];
  for (const candidate of input.preferences.dateTimePreferences) {
    if (
      !candidate ||
      typeof candidate !== "object" ||
      !isAppointmentLocalDate(candidate.date)
    )
      return null;
    const options = resolveAppointmentWaitlistTimeOptionsForDateForMode(
      input.publication,
      candidate.date,
      input.expectedMode,
    );
    if (!options || !candidate.time || !preferenceIsAllowed(candidate, options))
      return null;
    accepted.push({
      date: candidate.date,
      time: { ...candidate.time },
    } as AppointmentWaitlistDateTimePreference);
  }
  return {
    dateTimePreferences: accepted,
  } satisfies AppointmentWaitlistPreferences;
}


export function validateAppointmentWaitlistPreferencesForConfiguration(input: {
  publication: AppointmentWaitlistConfigurationPublicationV1;
  preferences: AppointmentWaitlistPreferences;
  maximumPreferences?: number;
}) {
  return validateAppointmentWaitlistPreferencesForConfigurationForMode({
    ...input,
    expectedMode: "individual",
  });
}

export function validateRetiredGroupAppointmentWaitlistPreferencesForConfigurationForStorageMigration(
  input: {
    publication: AppointmentWaitlistConfigurationPublicationV1;
    preferences: AppointmentWaitlistPreferences;
    maximumPreferences?: number;
  },
) {
  return validateAppointmentWaitlistPreferencesForConfigurationForMode({
    ...input,
    expectedMode: "group",
  });
}

export function createAppointmentWaitlistPreferencesFingerprint(input: {
  publicationId: string;
  preferences: AppointmentWaitlistPreferences;
}) {
  if (!isIdentifier(input.publicationId)) return null;
  const serialized = JSON.stringify({
    publicationId: input.publicationId,
    preferences: input.preferences,
  });
  return `appointment-waitlist-preferences-v1-${[0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35].map((seed) => hashFingerprint(serialized, seed)).join("")}`;
}

export function validateAppointmentWaitlistConflictPublication(input: {
  value: unknown;
  configuration: AppointmentWaitlistConfigurationPublicationV1;
  preferences: AppointmentWaitlistPreferences;
}): AppointmentWaitlistConflictPublicationV1 | null {
  const { value } = input;
  const fingerprint = createAppointmentWaitlistPreferencesFingerprint({
    publicationId: input.configuration.publicationId,
    preferences: input.preferences,
  });
  if (
    !fingerprint ||
    !isRecord(value) ||
    value.schemaVersion !== APPOINTMENT_WAITLIST_CONFLICT_SCHEMA_VERSION ||
    value.kind !== "appointment-waitlist-conflict" ||
    !isIdentifier(value.conflictPublicationId) ||
    value.publicationId !== input.configuration.publicationId ||
    value.waitlistDraftId !== input.configuration.waitlistDraftId ||
    !isProviderIdentity(value.providerIdentity) ||
    !providerIdentitiesMatch(
      value.providerIdentity,
      input.configuration.providerIdentity,
    ) ||
    !isEpoch(value.generatedAtEpochMs) ||
    !isEpoch(value.expiresAtEpochMs) ||
    value.generatedAtEpochMs < input.configuration.generatedAtEpochMs ||
    value.expiresAtEpochMs > input.configuration.expiresAtEpochMs ||
    value.expiresAtEpochMs <= value.generatedAtEpochMs ||
    value.preferencesFingerprint !== fingerprint ||
    !isRecord(value.result)
  )
    return null;
  let result: AppointmentWaitlistConflictPublicationV1["result"];
  if (value.result.kind === "none") result = { kind: "none" };
  else if (
    value.result.kind === "bookable" &&
    Array.isArray(value.result.matches) &&
    value.result.matches.length > 0
  ) {
    const matches = value.result.matches.map((match) =>
      isRecord(match) &&
      Number.isSafeInteger(match.preferenceIndex) &&
      (match.preferenceIndex as number) >= 0 &&
      (match.preferenceIndex as number) <
        input.preferences.dateTimePreferences.length &&
      match.date ===
        input.preferences.dateTimePreferences[match.preferenceIndex as number]
          ?.date
        ? {
            preferenceIndex: match.preferenceIndex as number,
            date: match.date as string,
          }
        : null,
    );
    if (
      matches.some((match) => match === null) ||
      new Set(matches.map((match) => match?.preferenceIndex)).size !==
        matches.length
    )
      return null;
    result = {
      kind: "bookable",
      matches: matches as { preferenceIndex: number; date: string }[],
    };
  } else return null;
  return {
    schemaVersion: APPOINTMENT_WAITLIST_CONFLICT_SCHEMA_VERSION,
    kind: "appointment-waitlist-conflict",
    conflictPublicationId: value.conflictPublicationId,
    publicationId: input.configuration.publicationId,
    waitlistDraftId: input.configuration.waitlistDraftId,
    providerIdentity: { ...input.configuration.providerIdentity },
    generatedAtEpochMs: value.generatedAtEpochMs,
    expiresAtEpochMs: value.expiresAtEpochMs,
    preferencesFingerprint: fingerprint,
    result,
  };
}

export function isAppointmentWaitlistConflictPublicationCurrent(input: {
  publication: AppointmentWaitlistConflictPublicationV1;
  configuration: AppointmentWaitlistConfigurationPublicationV1;
  preferences: AppointmentWaitlistPreferences;
  expectedProviderIdentity: AppointmentWaitlistProviderIdentity;
  evaluatedAtEpochMs?: number;
}) {
  return isAppointmentWaitlistConflictPublicationCurrentForMode({
    ...input,
    expectedMode: "individual",
  });
}

function isAppointmentWaitlistConflictPublicationCurrentForMode(input: {
  publication: AppointmentWaitlistConflictPublicationV1;
  configuration: AppointmentWaitlistConfigurationPublicationV1;
  preferences: AppointmentWaitlistPreferences;
  expectedProviderIdentity: AppointmentWaitlistProviderIdentity;
  evaluatedAtEpochMs?: number;
  expectedMode: AppointmentSchedulePlan["mode"];
}) {
  const accepted = validateAppointmentWaitlistConflictPublication({
    value: input.publication,
    configuration: input.configuration,
    preferences: input.preferences,
  });
  const now = input.evaluatedAtEpochMs ?? Date.now();
  return (
    Boolean(accepted) &&
    isAppointmentWaitlistConfigurationPublicationCurrentForMode({
      publication: input.configuration,
      expectedProviderIdentity: input.expectedProviderIdentity,
      evaluatedAtEpochMs: now,
      expectedMode: input.expectedMode,
    }) &&
    providerIdentitiesMatch(
      accepted!.providerIdentity,
      input.expectedProviderIdentity,
    ) &&
    accepted!.generatedAtEpochMs <= now + 5 * 60_000 &&
    accepted!.expiresAtEpochMs > now
  );
}

export function isRetiredGroupAppointmentWaitlistConflictPublicationCurrentForStorageMigration(
  input: {
    publication: AppointmentWaitlistConflictPublicationV1;
    configuration: AppointmentWaitlistConfigurationPublicationV1;
    preferences: AppointmentWaitlistPreferences;
    expectedProviderIdentity: AppointmentWaitlistProviderIdentity;
    evaluatedAtEpochMs?: number;
  },
) {
  return isAppointmentWaitlistConflictPublicationCurrentForMode({
    ...input,
    expectedMode: "group",
  });
}

function appointmentWaitlistConfigurationCoversPlanForMode(input: {
  publication: AppointmentWaitlistConfigurationPublicationV1;
  plan: AppointmentSchedulePlan;
  expectedMode: AppointmentSchedulePlan["mode"];
}) {
  const publication = validateAppointmentWaitlistConfigurationPublicationForMode(
    input.publication,
    input.expectedMode,
  );
  return Boolean(
    publication &&
    publication.planFingerprint ===
      createAppointmentWaitlistSchedulePlanFingerprint(input.plan) &&
    haveExactSerializableStructure(publication.plan, input.plan),
  );
}


export function appointmentWaitlistConfigurationCoversPlan(input: {
  publication: AppointmentWaitlistConfigurationPublicationV1;
  plan: AppointmentSchedulePlan;
}) {
  return appointmentWaitlistConfigurationCoversPlanForMode({
    ...input,
    expectedMode: "individual",
  });
}

export function retiredGroupAppointmentWaitlistConfigurationCoversPlanForStorageMigration(
  input: {
    publication: AppointmentWaitlistConfigurationPublicationV1;
    plan: AppointmentSchedulePlan;
  },
) {
  return appointmentWaitlistConfigurationCoversPlanForMode({
    ...input,
    expectedMode: "group",
  });
}
